import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useAppSelector } from '@shared/store';
import { useDesignWorkspace } from '@shared/designVariants/DesignWorkspaceProvider';
import {
  useCompanyControllerGetCompanyQuery,
  useCampaignControllerCampaignQuery,
  useCampaignAgentListControllerListQuery,
  useLlmModelListControllerModelListQuery,
  type CampaignListItemDto,
} from '@shared/services/apiService';
import { CampaignPicker } from './CampaignPicker';
import { CampaignResources } from './CampaignResources';
import { workspaceAgentPath } from './workspacePaths';

const ProviderModels = ({ id }: { id: string }) => {
  const query = useLlmModelListControllerModelListQuery(
    { id },
    { refetchOnMountOrArgChange: true },
  );
  if (query.isError)
    return (
      <Alert severity="error">
        Unable to load the current model catalog.{' '}
        <Button onClick={() => void query.refetch()}>Retry</Button>
      </Alert>
    );
  if (!query.currentData)
    return <CircularProgress size={18} aria-label="Loading provider models" />;
  return (
    <Stack direction="row" flexWrap="wrap" gap={1}>
      {query.currentData.length ? (
        query.currentData.map((model) => (
          <Chip key={model.id} label={model.title} variant="outlined" size="small" />
        ))
      ) : (
        <Typography variant="body2">
          No catalog models assigned. Custom model keys may be available for this provider.
        </Typography>
      )}
    </Stack>
  );
};
const CampaignServices = ({
  campaignId,
  request,
}: {
  campaignId: string;
  request: (action: () => void) => void;
}) => {
  const companyId = useAppSelector((state) => state.auth.authenticatedUser?.companyId);
  const campaign = useCampaignControllerCampaignQuery({ id: campaignId });
  const direct = useCampaignAgentListControllerListQuery({ id: campaignId });
  const workspace = useDesignWorkspace();
  if (campaign.isError || direct.isError)
    return (
      <Alert severity="error">
        Current campaign service assignments could not be loaded.{' '}
        <Button
          onClick={() => {
            void campaign.refetch();
            void direct.refetch();
          }}>
          Retry
        </Button>
      </Alert>
    );
  if (!campaign.currentData || !direct.currentData)
    return <CircularProgress aria-label="Loading campaign service assignments" />;
  if (campaign.currentData.companyId !== companyId)
    return <Alert severity="error">This campaign is unavailable in the current company.</Alert>;
  const agents = campaign.currentData.squad?.agents ?? direct.currentData;
  return (
    <Stack gap={2}>
      {agents.length ? (
        agents.map((agent) => (
          <Paper variant="outlined" key={agent.id} sx={{ p: 2 }}>
            <Stack gap={1}>
              <Typography fontWeight={600}>{agent.name}</Typography>
              <Typography variant="body2">Voice: {agent.voice?.name || agent.voiceId}</Typography>
              <Typography variant="body2">
                Transcriber: {agent.transcriber?.provider || agent.transcriberId}
              </Typography>
              <Typography variant="body2">
                Language model: {agent.llmProvider?.title || agent.llmProviderId} ·{' '}
                {agent.llmModel?.title ||
                  agent.llmCustomModelKey ||
                  agent.llmModelId ||
                  'Not configured'}
              </Typography>
              <Button
                onClick={() =>
                  request(() => workspace.navigate(workspaceAgentPath(campaignId, agent.id)))
                }>
                Configure this agent’s services
              </Button>
            </Stack>
          </Paper>
        ))
      ) : (
        <Alert severity="info">
          No agents are configured.{' '}
          <Button
            onClick={() =>
              request(() =>
                workspace.navigate(
                  workspaceAgentPath(campaignId, 'new', undefined, crypto.randomUUID()),
                ),
              )
            }>
            Create an assistant
          </Button>
        </Alert>
      )}
    </Stack>
  );
};

export const ConnectionsWorkspace = ({
  onDirtyChange,
  requestNavigation,
}: {
  onDirtyChange: (dirty: boolean) => void;
  requestNavigation: (action: () => void) => void;
}) => {
  const companyId = useAppSelector((state) => state.auth.authenticatedUser?.companyId);
  const query = useCompanyControllerGetCompanyQuery(
    { id: companyId || '' },
    { skip: !companyId, refetchOnMountOrArgChange: true },
  );
  const [tab, setTab] = useState('services');
  const [models, setModels] = useState<string | null>(null);
  const [campaign, setCampaign] = useState<CampaignListItemDto | null>(null);
  const [assignment, setAssignment] = useState('providers');
  const company = query.currentData;
  if (query.isError)
    return (
      <Alert severity="error">
        The company service catalog could not be loaded.{' '}
        <Button onClick={() => void query.refetch()}>Retry</Button>
      </Alert>
    );
  if (!company) return <CircularProgress aria-label="Loading current company services" />;
  return (
    <Stack gap={3}>
      <Typography color="text.secondary">
        Company-provisioned providers and saved campaign integrations. Service selection,
        integration configuration and runtime checks retain their native permissions.
      </Typography>
      <Tabs
        value={tab}
        onChange={(_, value) =>
          requestNavigation(() => {
            setTab(value);
            onDirtyChange(false);
          })
        }
        aria-label="Connection catalog sections">
        <Tab value="services" label="Available services" />
        <Tab value="assignments" label="Campaign assignments" />
      </Tabs>
      {tab === 'services' ? (
        <>
          <Typography variant="h5">Language models</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2 }}>
            {company.llmProviders === undefined ? (
              <Alert severity="warning">
                The current company response does not include its language-model catalog.
              </Alert>
            ) : company.llmProviders.length ? (
              company.llmProviders.map((provider) => (
                <Paper key={provider.id} variant="outlined" sx={{ p: 3 }}>
                  <Stack gap={2}>
                    <Typography variant="h5">{provider.title}</Typography>
                    <Typography variant="body2">
                      {provider.allowsCustomModels
                        ? 'Supports custom model keys'
                        : 'Uses the company model catalog'}
                    </Typography>
                    <Button onClick={() => setModels(models === provider.id ? null : provider.id)}>
                      {models === provider.id ? 'Hide models' : 'Inspect models'}
                    </Button>
                    {models === provider.id && <ProviderModels id={provider.id} />}
                  </Stack>
                </Paper>
              ))
            ) : (
              <Alert severity="info">
                No language-model providers are provisioned for this company.
              </Alert>
            )}
          </Box>
          <Typography variant="h5">Voice generation</Typography>
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {company.voices === undefined ? (
              <Alert severity="warning">
                The current company response does not include its voice catalog.
              </Alert>
            ) : company.voices.length ? (
              company.voices.map((voice) => (
                <Chip
                  key={voice.id}
                  label={`${voice.name} · ${voice.provider}`}
                  variant="outlined"
                />
              ))
            ) : (
              <Alert severity="info">No voices are provisioned for this company.</Alert>
            )}
          </Stack>
          <Typography variant="h5">Transcription</Typography>
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {company.transcribers === undefined ? (
              <Alert severity="warning">
                The current company response does not include its transcription catalog.
              </Alert>
            ) : company.transcribers.length ? (
              company.transcribers.map((transcriber) => (
                <Chip
                  key={transcriber.id}
                  label={`${transcriber.providerDisplayName || transcriber.provider} · ${transcriber.language || 'Language configured by provider'} · ${transcriber.model || transcriber.kind}`}
                  variant="outlined"
                />
              ))
            ) : (
              <Alert severity="info">No transcribers are provisioned for this company.</Alert>
            )}
          </Stack>
          <Button variant="contained" onClick={() => setTab('assignments')}>
            Review campaign assignments
          </Button>
          <Typography variant="caption" color="text.secondary">
            Availability in this catalog means provisioned access. It does not assert successful
            credential verification or current provider health.
          </Typography>
        </>
      ) : (
        <>
          <CampaignPicker
            value={campaign}
            onChange={(next) =>
              requestNavigation(() => {
                setCampaign(next);
                onDirtyChange(false);
              })
            }
          />
          {campaign && (
            <>
              <Tabs
                value={assignment}
                onChange={(_, value) =>
                  requestNavigation(() => {
                    setAssignment(value);
                    onDirtyChange(false);
                  })
                }
                variant="scrollable"
                scrollButtons="auto"
                aria-label="Campaign service sections">
                <Tab value="providers" label="Agent services" />
                <Tab value="integrations" label="Saved integrations" />
              </Tabs>
              {assignment === 'providers' ? (
                <CampaignServices
                  key={campaign.id}
                  campaignId={campaign.id}
                  request={requestNavigation}
                />
              ) : (
                <CampaignResources
                  key={campaign.id}
                  module="Connections"
                  campaignId={campaign.id}
                  onDirtyChange={onDirtyChange}
                  requestNavigation={requestNavigation}
                />
              )}
            </>
          )}
        </>
      )}
    </Stack>
  );
};
